import React, {useRef, useMemo, useEffect} from "react"
import { Outlet } from "react-router-dom"
import { Layout, theme } from "antd"

import ReleaseAlert from "@components/ReleaseAlert"
import { getConfig } from "@components/Config"
import useProjectsStore from "@stores/useProjectsStore"
import usePartnersStore from "@stores/usePartnersStore"
import { useAppContext } from "@components/AppContext"
import { ProjectCreateModal } from "@modules/backstage/projects"
import { SIDEBAR_WIDTH, ALERT_HEIGHT, isMobile } from "@components/constants"
import {
  useUsersStore,
  useOptionsStore,
  useAccountsStore,
  useCampaignsStore,
  useBankAccountsStore,
  useFailedTransactionsStore
} from "@stores/backstage"

import Sidebar from "./Sidebar"
import SecondFactorAlert from "./SecondFactorAlert"
import InvalidBankAccountAlert, { hasInvalidBankAccounts } from "./InvalidBankAccountAlert"

const is2FaRequired = getConfig('is2FaRequired')

const BackstageLayout = () => {
  const createProjectModal = useRef({})

  const { token } = theme.useToken()
  const { colorBgContainer } = token

  const usersStore = useUsersStore()
  const optionsStore = useOptionsStore()
  const projectsStore = useProjectsStore()
  const partnersStore = usePartnersStore()
  const accountsStore = useAccountsStore()
  const campaignsStore = useCampaignsStore()
  const failedTransactionsStore = useFailedTransactionsStore()

  const {
    projects,
    addProject,
  } = projectsStore

  const bankAccountsStore = useBankAccountsStore(projects)
  const { identity } = useAppContext()

  const openCreateProjectModal = () =>
    createProjectModal.current.open()

  const openCreateOraganizationModal = () => {}

  useEffect(() => {
    if (isMobile) {
      document
        .querySelector('meta[name="viewport"]')
        .setAttribute("content", "width=1400")
    }
  }, [])

  const {
    brokers,
    members,
  } = usersStore

  const {
    funds,
    fundsMap,
    sponsors,
    sponsorsMap,
  } = partnersStore

  const {
    failedTransactions
  } = failedTransactionsStore

  const { bankAccounts } = bankAccountsStore
  const { is2FaEnabled } = identity

  const hasInvalidBankAccountsAlert = hasInvalidBankAccounts(bankAccounts, projects)

  const layoutAlertsCount = [
    is2FaRequired && !is2FaEnabled,
    hasInvalidBankAccountsAlert
  ].filter(value => value).length

  const layoutTopOffset = layoutAlertsCount * ALERT_HEIGHT

  const context = {
    ...usersStore,
    ...optionsStore,
    ...projectsStore,
    ...partnersStore,
    ...accountsStore,
    ...campaignsStore,
    ...bankAccountsStore,
    ...failedTransactionsStore,
    layoutTopOffset,
  }

  const isLoading = [
    projects,
    brokers,
    members,
    funds,
    fundsMap,
    sponsors,
    sponsorsMap,
  ].includes(undefined)

  const failedTransactionsCount = useMemo(
    () => (failedTransactions || []).length
  , [ failedTransactions ])

  const isFullscreenMode = window.location.pathname.startsWith('/backstage/campaigns/CAM_')

  const layoutProps = {
    style: { background: colorBgContainer }
  }

  if (!isFullscreenMode) {
    layoutProps.style.marginLeft = SIDEBAR_WIDTH
  }

  const sidebarProps = {
    projects,
    openCreateProjectModal,
    failedTransactionsCount,
    openCreateOraganizationModal,
  }

  return (
    <>
      {
        !isFullscreenMode && (
          <Sidebar {...sidebarProps} />
        )
      }

      <Layout {...layoutProps}>
        <Layout.Content>
          {
            !isFullscreenMode && (
              <>
                <ReleaseAlert />
                <SecondFactorAlert />
                <InvalidBankAccountAlert
                  projects={projects}
                  bankAccounts={bankAccounts}/>
              </>
            )
          }

          {!isLoading && <Outlet context={context} /> }
        </Layout.Content>
      </Layout>

      <ProjectCreateModal
        modal={createProjectModal}
        addProject={addProject}
      />
    </>
  )
}

export default BackstageLayout
